import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  FileText,
  Pencil,
  PlayCircle,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import {
  useActivatePlan,
  useCustomerMaintenancePlans,
  useGeneratePlan,
  useUpdatePlan,
} from '@/hooks/useMaintenancePlan';
import { formatDateTime } from '@/lib/format';
import type {
  MaintenancePlan,
  MaintenancePlanItem,
  MaintenancePlanItemFrequency,
} from '@/types/api';

/**
 * Wartungsplan-Tab pro Kunde (WT-204, Sprint 2).
 *
 * - Aktueller Plan = neuester nicht-archivierter Plan
 * - History = alle archivierten (Collapse)
 * - Edit-Dialog setzt Frequenz pro Item; Plan-Activate-Button macht
 *   den Status active (=> Sprint-5-Run-Engine kann ihn ausführen)
 */

/**
 * Voller Wertebereich seit dem Katalog-Umbau (10.09.2026): der Generator setzt
 * am Item die Frequenz des Kunden, und die kennt auch biannual/yearly/custom.
 * Stand hier nur die alte Vierer-Liste, ließe sich ein halbjährlich
 * gewarteter Kunde im Dialog nicht auf seiner eigenen Frequenz speichern.
 */
const FREQUENCIES: MaintenancePlanItemFrequency[] = [
  'weekly',
  'monthly',
  'quarterly',
  'biannual',
  'yearly',
  'on_demand',
  'custom',
];

interface Props {
  customerId: string;
}

export function MaintenancePlanTab({ customerId }: Props): JSX.Element {
  const plansQuery = useCustomerMaintenancePlans(customerId);
  const generate = useGeneratePlan();
  const activate = useActivatePlan();

  const { current, history } = useMemo(
    () => splitPlans(plansQuery.data?.data ?? []),
    [plansQuery.data]
  );

  if (plansQuery.isLoading) return <SkeletonRows rows={4} cols={3} />;
  if (plansQuery.isError) return <ErrorAlert error={plansQuery.error} />;

  if (!current) {
    return (
      <Card>
        <EmptyState
          icon={<Sparkles className="size-8" />}
          title="Noch kein Wartungsplan"
          description="Aus dem aktiven Vertrag (Service-Profil) leiten wir einen Vorschlag mit Routinen + Default-Frequenzen ab. Du kannst alles vor Aktivierung überschreiben."
          actions={
            <Button
              leftIcon={<Sparkles className="size-4" />}
              loading={generate.isPending}
              onClick={() => void generate.mutate({ customerId })}
            >
              Plan generieren
            </Button>
          }
        />
        {generate.isError && <ErrorAlert error={generate.error} className="mt-3" />}
      </Card>
    );
  }

  // Plan generiert, aber keine Items abgeleitet -> meist fehlt Vertrags-Mapping.
  if (current.items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<AlertTriangle className="size-8 text-warning" />}
          title="Keine Routinen ableitbar"
          description="Möglicherweise fehlt das Vertrags-Mapping. Du kannst das Mapping für diesen Kunden setzen oder direkt aus den Systemen einen Plan ableiten."
          actions={
            <>
              <Link
                to="/settings/contracts/mapping"
                search={{ customer: customerId }}
              >
                <Button variant="secondary" leftIcon={<FileText className="size-4" />}>
                  Vertrag zuordnen
                </Button>
              </Link>
              <Button
                leftIcon={<Sparkles className="size-4" />}
                loading={generate.isPending}
                onClick={() => void generate.mutate({ customerId })}
              >
                Aus Systemen generieren
              </Button>
            </>
          }
        />
        {generate.isError && <ErrorAlert error={generate.error} className="mt-3" />}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PlanCard
        plan={current}
        onActivate={() => activate.mutate(customerId)}
        activating={activate.isPending}
        onRegenerate={() => generate.mutate({ customerId })}
        regenerating={generate.isPending}
      />
      {generate.isError && <ErrorAlert error={generate.error} />}
      {activate.isError && <ErrorAlert error={activate.error} />}

      {history.length > 0 && (
        <Card title="Plan-History" description={`${history.length} archivierte Versionen`}>
          <details>
            <summary className="cursor-pointer text-sm text-primary hover:underline">
              Älteste Pläne anzeigen
            </summary>
            <ul className="mt-2 flex flex-col gap-2">
              {history.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between rounded-md border border-border p-2 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Archive className="size-4 text-muted" aria-hidden="true" />
                    <span>{p.name ?? `Plan ${p.id.slice(0, 8)}`}</span>
                    <Badge tone="neutral">{p.status}</Badge>
                  </span>
                  <span className="text-xs text-muted">
                    {p.items.length} Items · {formatDateTime(p.updated_at)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </Card>
      )}
    </div>
  );
}

function splitPlans(plans: MaintenancePlan[]): {
  current: MaintenancePlan | null;
  history: MaintenancePlan[];
} {
  const sorted = [...plans].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
  const current = sorted.find((p) => p.status !== 'archived') ?? null;
  const history = sorted.filter((p) => p.status === 'archived');
  return { current, history };
}

function PlanCard({
  plan,
  onActivate,
  activating,
  onRegenerate,
  regenerating,
}: {
  plan: MaintenancePlan;
  onActivate: () => void;
  activating: boolean;
  onRegenerate: () => void;
  regenerating: boolean;
}): JSX.Element {
  const update = useUpdatePlan();
  const [editing, setEditing] = useState<{ index: number; item: MaintenancePlanItem } | null>(
    null
  );

  const replaceItem = async (
    index: number,
    next: MaintenancePlanItem
  ): Promise<void> => {
    const items = [...plan.items];
    items[index] = next;
    await update.mutateAsync({ planId: plan.id, body: { items } });
    setEditing(null);
  };

  const status = plan.status ?? 'draft';
  const isEditable = status === 'draft' || status === 'proposed';
  const canActivate = isEditable;

  const columns = useMemo((): ReadonlyArray<Column<MaintenancePlanItem>> => {
    return [
      {
        id: 'routine',
        header: 'Routine',
        className: 'font-mono text-xs',
        cell: (it) => it.routine_slug,
      },
      {
        id: 'service-type',
        header: 'Service-Type',
        filter: 'select',
        filterValue: (it) => it.service_type ?? '',
        cell: (it) => <Badge tone="neutral">{it.service_type ?? '–'}</Badge>,
      },
      {
        id: 'default-frequenz',
        header: 'Default-Frequenz',
        filter: 'select',
        filterValue: (it) => it.frequency,
        cell: (it) => <Badge tone="primary">{it.frequency}</Badge>,
      },
      {
        id: 'hinweis',
        header: 'Hinweis (Quelle)',
        className: 'whitespace-pre-wrap text-xs text-muted',
        filterValue: (it) => it.notes ?? '',
        cell: (it) => it.notes ?? '–',
      },
      {
        id: 'aktion',
        header: 'Aktion',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (it) => {
          // cell bekommt keinen Index; Referenzgleichheit trifft dasselbe Item.
          const idx = plan.items.findIndex((item) => item === it);
          return (
            <span className="flex justify-end gap-1">
              <Link
                to="/runs/new"
                search={{ routine: it.routine_slug, customers: plan.customer_id }}
              >
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<PlayCircle className="size-3" />}
                  data-testid={`plan-item-run-${it.routine_slug}`}
                >
                  Jetzt
                </Button>
              </Link>
              <Link to="/ai/suggest" search={{ customer: plan.customer_id }}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Sparkles className="size-3" />}
                  title="KI-Vorschlag für diese Routine generieren"
                  data-testid={`plan-item-ai-${it.routine_slug}`}
                >
                  KI
                </Button>
              </Link>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Pencil className="size-3" />}
                disabled={!isEditable}
                onClick={() => {
                  if (idx < 0) return;
                  setEditing({ index: idx, item: it });
                }}
              >
                Edit
              </Button>
            </span>
          );
        },
      },
    ];
  }, [isEditable, plan.customer_id, plan.items]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>{plan.name ?? 'Wartungsplan'}</span>
          <PlanStatusBadge status={status} />
        </span>
      }
      description={`Aktualisiert ${formatDateTime(plan.updated_at)} · ${plan.items.length} Routinen`}
      actions={
        <>
          <Button
            variant="secondary"
            leftIcon={<Sparkles className="size-4" />}
            onClick={onRegenerate}
            loading={regenerating}
          >
            Neu generieren
          </Button>
          <Button
            leftIcon={<PlayCircle className="size-4" />}
            disabled={!canActivate}
            loading={activating}
            onClick={onActivate}
          >
            Plan aktivieren
          </Button>
        </>
      }
    >
      <DataTable
        caption="Wartungsplan-Items"
        columns={columns}
        rows={plan.items}
        rowKey={(it, idx) => `${it.routine_slug}-${idx}`}
        storageKey="wartungsplan-items"
      />

      {update.isError && <ErrorAlert error={update.error} className="mt-3" />}
      {!isEditable && (
        <p className="mt-3 inline-flex items-center gap-1 text-xs text-success">
          <CheckCircle2 className="size-3" />
          Plan ist aktiv - Änderungen erfordern Re-Generation.
        </p>
      )}

      <EditItemDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onSave={(next) => editing && void replaceItem(editing.index, next)}
        saving={update.isPending}
      />
    </Card>
  );
}

// Status als string, weil das Backend den echten Wert "active" liefert (nicht
// "accepted" wie in den generierten Wire-Typen). Bleibt so robust gegen beide.
function PlanStatusBadge({ status }: { status: string }): JSX.Element {
  switch (status) {
    case 'active':
    case 'accepted':
      return <Badge tone="success">aktiv</Badge>;
    case 'proposed':
      return <Badge tone="primary">Vorschlag</Badge>;
    case 'draft':
      return <Badge tone="warning">Entwurf</Badge>;
    case 'archived':
      return <Badge tone="neutral">archiviert</Badge>;
    default:
      return <Badge tone="neutral">{status}</Badge>;
  }
}

interface EditItemDialogProps {
  editing: { index: number; item: MaintenancePlanItem } | null;
  onClose: () => void;
  onSave: (next: MaintenancePlanItem) => void;
  saving: boolean;
}

function EditItemDialog({ editing, onClose, onSave, saving }: EditItemDialogProps): JSX.Element {
  const [draft, setDraft] = useState<MaintenancePlanItem | null>(null);

  // Synchronise draft mit editing-Item beim Öffnen.
  useEffect(() => {
    if (editing) setDraft(editing.item);
    else setDraft(null);
  }, [editing]);

  if (!editing || !draft) {
    return (
      <Dialog open={false} onClose={onClose} title="Item bearbeiten">
        <span />
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Plan-Item bearbeiten"
      description={`Routine: ${editing.item.routine_slug}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onSave(draft)}
            loading={saving}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Frequenz</span>
          <select
            value={draft.frequency}
            onChange={(e) =>
              setDraft((d) =>
                d ? { ...d, frequency: e.target.value as MaintenancePlanItemFrequency } : d
              )
            }
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="Hinweis / Severity"
          value={draft.notes ?? ''}
          onChange={(e) => setDraft((d) => (d ? { ...d, notes: e.target.value } : d))}
          hint="Wird im Plan als Begründung / Severity-Note angezeigt."
        />
      </div>
    </Dialog>
  );
}
